import { isValidUserId, serializePlan, serializePlanForWrite, normalizePlanGroups, TRAINING_MODULE, TRAINING_SCHEMA_VERSION } from './model';

export const PUBLICATION_COLLECTION = 'allenamentiPubblicati';
export const TRAINING_ACCESS_COLLECTION = 'trainingAccess';
export const PUBLICATION_MODULE = 'allenamenti-pubblicati';
export const PUBLICATION_SCHEMA_VERSION = 1;

// Legacy pilot fixture for maintenance scripts. Client visibility follows its own publication.
export const PILOT_UID = 'WWsrsIdYgfMcSl0N8YPgLqL3eHB3';
export const PILOT_EMAIL = 'valerio@fusco.fit';

const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const isRevision = value => Number.isSafeInteger(value) && value >= 1;
const fail = (code, message) => { throw Object.assign(new Error(message), { code: `training/${code}` }); };

function checkUserId(userId) {
  if (!isValidUserId(userId)) fail('identity', 'Il riferimento al cliente non è valido.');
}

function checkPlanIdentity(plan, userId) {
  if (!isRecord(plan) || plan.module !== TRAINING_MODULE || plan.schemaVersion !== TRAINING_SCHEMA_VERSION) {
    fail('incompatible', 'Il formato dell’allenamento da pubblicare non è supportato.');
  }
  if (plan.userId !== userId) fail('identity', 'L’allenamento appartiene a un altro cliente.');
  if (plan.status !== 'ready') fail('not-ready', 'Segna l’allenamento come pronto e salvalo prima di pubblicarlo.');
}

function timestampIso(value) {
  if (value === null) return null;
  let date = value;
  try {
    if (typeof value?.toDate === 'function') date = value.toDate();
    else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) date = new Date(value);
    if (date instanceof Date && Number.isFinite(date.getTime())) return date.toISOString();
  } catch { /* Invalid timestamps are rejected with the publication error below. */ }
  fail('corrupt', 'La data di pubblicazione dell’allenamento non è valida.');
}

// serializePlan validates every prescription and deeply clones its whitelisted fields.
// Remove import/audit attribution while retaining all details used by the client view.
function projectClientPlan(plan, { forWrite = false } = {}) {
  const snapshot = forWrite ? serializePlanForWrite(plan) : serializePlan(plan);
  delete snapshot.source;
  if (snapshot.details) {
    for (const field of ['createdBy', 'lastUpdate', 'sourceId', 'whoCanUse']) delete snapshot.details[field];
  }
  for (const workout of snapshot.workouts) {
    for (const exercise of workout.exercises) {
      if (exercise.details) delete exercise.details.sourceId;
    }
  }
  return snapshot;
}

/** Build a detached snapshot of the saved current plan; authorization lives in the store/rules. */
export function buildPublishedTraining(plan, {
  userId,
  sourceRevision,
  publicationRevision,
  publishedBy,
  publishedAt = null,
} = {}) {
  checkUserId(userId);
  checkPlanIdentity(plan, userId);
  if (plan.kind !== undefined && plan.kind !== 'current') {
    fail('archive-readonly', 'Pubblica l’allenamento corrente salvato, non una scheda dello storico.');
  }
  if (!isRevision(sourceRevision) || !isRevision(plan.revision) || plan.revision !== sourceRevision || !isRevision(publicationRevision)) {
    fail('conflict', 'La versione dell’allenamento da pubblicare non è valida. Ricarica la scheda.');
  }
  if (!isValidUserId(publishedBy)) fail('identity', 'Il riferimento al trainer che pubblica non è valido.');
  return {
    module: PUBLICATION_MODULE,
    schemaVersion: PUBLICATION_SCHEMA_VERSION,
    userId,
    sourceRevision,
    publicationRevision,
    publishedBy,
    // The store replaces this serializable value with a server timestamp when writing.
    publishedAt: timestampIso(publishedAt),
    plan: projectClientPlan(plan, { forWrite: true }),
  };
}

/** Validate the complete published record before exposing a detached client plan. */
export function readPublishedTraining(data, userId) {
  checkUserId(userId);
  if (!isRecord(data) || data.module !== PUBLICATION_MODULE || data.schemaVersion !== PUBLICATION_SCHEMA_VERSION || data.userId !== userId) {
    fail('incompatible', 'L’allenamento pubblicato ha un formato o un riferimento cliente non valido.');
  }
  if (!isRevision(data.sourceRevision) || !isRevision(data.publicationRevision) || !isValidUserId(data.publishedBy)) {
    fail('corrupt', 'I dati della pubblicazione dell’allenamento non sono validi.');
  }
  try {
    checkPlanIdentity(data.plan, userId);
    // Compatibility is a detached read projection. Stored publications and run snapshots stay untouched.
    const plan = normalizePlanGroups(projectClientPlan(data.plan), { mode: 'rounds' }).plan;
    return {
      module: PUBLICATION_MODULE,
      schemaVersion: PUBLICATION_SCHEMA_VERSION,
      userId,
      sourceRevision: data.sourceRevision,
      publicationRevision: data.publicationRevision,
      publishedBy: data.publishedBy,
      publishedAt: timestampIso(data.publishedAt),
      plan,
    };
  } catch (error) {
    if (error?.code === 'training/corrupt') throw error;
    fail('corrupt', 'L’allenamento pubblicato contiene dati non validi. Chiedi al trainer di verificarlo.');
  }
}
